/**
 * Success Stories Admin Index Page - List and manage success stories
 * @file src/web/features/achievements/pages/admin/index.tsx
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { useSuccessStories, type SuccessStory } from '../../hooks/useSuccessStories';
import { SEO } from '../../../../shared/components';
import {
  Award,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  User,
  Star,
  X,
  Calendar,
  GraduationCap,
  Quote,
  School
} from 'lucide-react';

const SuccessStoriesAdminPage: React.FC = () => {
  const { stories, loading, error, deleteStory, updateStory, refetch } = useSuccessStories(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [viewingStory, setViewingStory] = useState<SuccessStory | null>(null);

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}'s story"? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(id);
    try {
      await deleteStory(id);
    } catch (err: any) {
      alert(err.message || 'Failed to delete success story');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    setTogglingId(id);
    try {
      await updateStory(id, { isActive: !currentStatus });
    } catch (err: any) {
      alert(err.message || 'Failed to update success story');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          <span className="ml-2 text-gray-600">Loading success stories...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Manage Success Stories - Admin Panel" />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    Success Stories Management
                  </h1>
                  <p className="mt-1 text-amber-100 text-sm">
                    Manage student success stories and achievements
                  </p>
                </div>
              </div>
              <Link to="/achievements/admin/create">
                <Button className="bg-white text-amber-600 hover:bg-amber-50">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Story
                </Button>
              </Link>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
              <button onClick={refetch} className="ml-2 underline">Retry</button>
            </div>
          )}

          {/* Success Stories List */}
          <Card className="border-t-4 border-t-amber-500 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Star className="h-5 w-5 text-amber-600" />
                </div>
                Success Stories ({stories.length})
              </CardTitle>
              <CardDescription className="mt-1.5">
                Click on a story to edit its details
              </CardDescription>
            </div>

            <CardContent className="p-0">
              {stories.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Award className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p>No success stories yet.</p>
                  <Link to="/achievements/admin/create" className="text-amber-600 hover:underline mt-2 inline-block">
                    Add your first success story
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {stories.map((story) => (
                    <div
                      key={story.id}
                      className={`p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                        !story.isActive ? 'opacity-60 bg-gray-50' : ''
                      }`}
                    >
                      {/* Student Image */}
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        {story.imageUrl ? (
                          <img
                            src={story.imageUrl}
                            alt={story.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-amber-100">
                            <User className="w-8 h-8 text-amber-600" />
                          </div>
                        )}
                      </div>

                      {/* Story Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 truncate">{story.name}</h3>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {story.score}
                          </span>
                          {!story.isActive && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              Hidden
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-amber-600 font-medium">{story.board} - {story.year}</p>
                        {story.currentSchool && (
                          <p className="text-sm text-gray-500 truncate">{story.currentSchool}</p>
                        )}
                      </div>

                      {/* Order */}
                      <div className="text-sm text-gray-400">
                        Order: {story.order}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingStory(story)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>

                        <button
                          onClick={() => handleToggleActive(story.id, story.isActive)}
                          disabled={togglingId === story.id}
                          className={`p-2 rounded-lg transition-colors ${
                            story.isActive
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={story.isActive ? 'Hide story' : 'Show story'}
                        >
                          {togglingId === story.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : story.isActive ? (
                            <Star className="w-5 h-5 fill-current" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>

                        <Link
                          to={`/achievements/admin/edit/${story.id}`}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit story"
                        >
                          <Edit className="w-5 h-5" />
                        </Link>

                        <button
                          onClick={() => handleDelete(story.id, story.name)}
                          disabled={deletingId === story.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete story"
                        >
                          {deletingId === story.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* View Detail Modal */}
      {viewingStory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Award className="w-6 h-6" />
                  {viewingStory.name}'s Success Story
                </h2>
                <button
                  onClick={() => setViewingStory(null)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Image and Basic Info */}
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="w-32 h-32 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 mx-auto sm:mx-0">
                  {viewingStory.imageUrl ? (
                    <img
                      src={viewingStory.imageUrl}
                      alt={viewingStory.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-amber-100">
                      <User className="w-12 h-12 text-amber-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-2xl font-bold text-gray-900">{viewingStory.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                    <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                      {viewingStory.score}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                      {viewingStory.board}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      viewingStory.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {viewingStory.isActive ? 'Active' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Year
                  </div>
                  <p className="font-medium text-gray-900">{viewingStory.year}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <GraduationCap className="w-4 h-4" />
                    Board/Curriculum
                  </div>
                  <p className="font-medium text-gray-900">{viewingStory.board}</p>
                </div>
              </div>

              {/* Current School */}
              {viewingStory.currentSchool && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-600 text-sm mb-1">
                    <School className="w-4 h-4" />
                    Current Status
                  </div>
                  <p className="text-gray-900">{viewingStory.currentSchool}</p>
                </div>
              )}

              {/* Quote */}
              {viewingStory.quote && (
                <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                  <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                    <Quote className="w-4 h-4" />
                    Student Testimonial
                  </div>
                  <p className="text-gray-700 italic">"{viewingStory.quote}"</p>
                </div>
              )}

              {/* Meta Info */}
              <div className="text-sm text-gray-500 border-t pt-4 space-y-1">
                <p><span className="font-medium">Order:</span> {viewingStory.order}</p>
                <p><span className="font-medium">Created:</span> {new Date(viewingStory.createdAt).toLocaleString()}</p>
                <p><span className="font-medium">Updated:</span> {new Date(viewingStory.updatedAt).toLocaleString()}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Link
                  to={`/achievements/admin/edit/${viewingStory.id}`}
                  className="flex-1"
                >
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Story
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => setViewingStory(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuccessStoriesAdminPage;
